// ============================================================
// ELC Shift — Uploads / Areas module (v103.0)
//
// Geographic area management (Dubai, Abu Dhabi, Qatar, etc.).
// Areas group stores for KPI aggregation + area-manager assignment
// (one AM for Dubai, one UAE-wide, one for Qatar).
//
// Public surface:
//   • loadAreasFromDB (async)  — fetch areas list from Supabase
//   • renderAreas              — directory render in admin tab
//   • saveArea (async)         — create or update an area
//   • deleteArea (async)       — remove an area (soft if linked)
//
// Distinct from Uploads.kpi (which renders performance metrics per
// area). This module is area-definition CRUD only.
//
// Free globals consumed:
//   DB, showToast, escapeHtml, ALL_STORES
// ============================================================

export async function loadAreasFromDB() {
  try {
    const rows = await DB.areas.list();
    if (rows && rows.length) {
      // PostgREST returns the full row. We expect at minimum {id, name}, with
      // optional `country` for the display label.
      ALL_AREAS = rows.map(r => ({
        id: r.id,
        name: r.name || r.id,
        country: r.country || '',
      }));
      dlog('[DB] Loaded ' + ALL_AREAS.length + ' areas from database');
    } else {
      dlog('[DB] Areas list empty — keeping fallback');
    }
  } catch(e) {
    console.warn('[DB] Areas load failed, keeping hardcoded fallback:', e.message);
  }
}

export function renderAreas() {
  const tbody = document.getElementById('areasBody');
  if (!tbody) return;
  if (!Array.isArray(ALL_AREAS) || !ALL_AREAS.length) {
    tbody.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:24px;color:var(--sub)">No areas yet — tap + Add Area to create one.</td></tr>';
    return;
  }

  tbody.innerHTML = ALL_AREAS.map(a => {
    const mgr = getActiveAreaManagerAccount(a.id);
    const mgrName = mgr ? escapeHtml(mgr.name || '') : '<span style="color:var(--sub);font-style:italic">Unassigned</span>';
    const storeCount = getAreaStoreCount(a.id);
    const staffCount = getAreaStaffCount(a.id);
    const canDelete = storeCount === 0 && staffCount === 0;
    const deleteTooltip = canDelete
      ? 'Delete this area'
      : `Can't delete: ${storeCount} store${storeCount===1?'':'s'} and ${staffCount} staff still assigned`;
    const deleteStyle = canDelete
      ? 'color:var(--red);border-color:var(--red);margin-inline-start:4px'
      : 'color:var(--tiny);border-color:var(--border);margin-inline-start:4px;cursor:not-allowed';
    const deleteAttrs = canDelete
      ? `onclick="Uploads.areas.deleteArea('${escapeHtml(a.id)}')"`
      : `disabled title="${escapeHtml(deleteTooltip)}"`;

    return `<tr>
      <td><code style="font-size:12px">${escapeHtml(a.id)}</code></td>
      <td><strong>${escapeHtml(a.name||'')}</strong></td>
      <td style="font-size:13px">${escapeHtml(a.region||'')}</td>
      <td style="font-size:13px">${escapeHtml(a.country||'')}</td>
      <td style="font-size:13px"${mgr ? ' translate="no"' : ''}>${mgrName}</td>
      <td style="text-align:center">${storeCount}</td>
      <td style="text-align:center">${staffCount}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-outline btn-sm" onclick="openEditAreaModal('${escapeHtml(a.id)}')">Edit</button>
        <button class="btn btn-outline btn-sm" style="${deleteStyle}" ${deleteAttrs} title="${escapeHtml(deleteTooltip)}">Remove</button>
      </td>
    </tr>`;
  }).join('');
}

export async function saveArea() {
  const id        = document.getElementById('areaId').value.trim();
  const name      = document.getElementById('areaName').value.trim();
  const region    = document.getElementById('areaRegion').value.trim();
  const country   = document.getElementById('areaCountry').value;
  const editingId = document.getElementById('areaEditId').value;
  const isEdit    = !!editingId;

  // Validation
  if (!id)      { showToast('Enter an Area ID', 'error');     return; }
  if (!name)    { showToast('Enter an area name', 'error');   return; }
  if (!region)  { showToast('Enter a region', 'error');       return; }
  if (!country) { showToast('Pick a country', 'error');       return; }
  if (!isEdit) {
    // Check uniqueness on add
    const exists = (ALL_AREAS || []).some(a => String(a.id) === String(id));
    if (exists) { showToast('Area ID "' + id + '" already exists', 'error'); return; }
  }

  try {
    if (isEdit) {
      await DB.areas.update(id, { name, region, country });
    } else {
      await DB.areas.create({ id, name, region, country });
    }

    // Manager reassignment — only on edit, only if the dropdown changed
    if (isEdit) {
      const prevMgrName = document.getElementById('areaPrevManagerName').value;
      const newMgrName  = document.getElementById('areaManagerSelect').value; // '' = Unassigned
      if (prevMgrName !== newMgrName) {
        const accounts = loadAccounts() || [];
        // Unassign the previous manager (if any) by clearing their area
        if (prevMgrName) {
          const prev = accounts.find(a => a.name === prevMgrName);
          if (prev && prev.id) {
            try { await DB.accounts.update(prev.id, { area: null }); }
            catch(e) { console.warn('[Areas] Failed to unassign previous manager:', e.message); }
          }
        }
        // Assign the new manager (if not Unassigned)
        if (newMgrName) {
          const next = accounts.find(a => a.name === newMgrName);
          if (next && next.id) {
            try { await DB.accounts.update(next.id, { area: id, role: 'area_manager' }); }
            catch(e) { console.warn('[Areas] Failed to assign new manager:', e.message); }
          }
        }
      }
    }

    closeAreaModal();
    // Reload areas + accounts so derived state (current manager) is fresh
    await loadAreasFromDB();
    await loadAccountsFromDB();
    renderAreas();
    populateAreaSelects();  // refresh every area-related dropdown elsewhere in admin
    showToast(isEdit ? 'Area updated' : 'Area created', 'success');
  } catch (e) {
    console.error('[Areas] Save failed:', e);
    showToast('Save failed: ' + (e.message || 'unknown error'), 'error');
  }
}

export async function deleteArea(id) {
  const a = (ALL_AREAS || []).find(x => String(x.id) === String(id));
  if (!a) { showToast('Area not found', 'error'); return; }
  // Defensive recheck — even though the button is disabled when FKs exist,
  // counts could have changed since render. Refuse the delete server-side
  // anyway via the FK constraint, but a friendlier message here.
  const storeCount = getAreaStoreCount(id);
  const staffCount = getAreaStaffCount(id);
  if (storeCount > 0 || staffCount > 0) {
    showToast(
      `Can't delete — ${storeCount} stores and ${staffCount} staff still assigned to ${a.name}`,
      'error'
    );
    return;
  }
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })(`Delete area "${a.name}"? This cannot be undone.`, { danger: true, confirmLabel: 'Delete' }))) return;
  try {
    await DB.areas.remove(id);
    await loadAreasFromDB();
    renderAreas();
    populateAreaSelects();
    showToast(a.name + ' deleted', 'success');
  } catch (e) {
    console.error('[Areas] Delete failed:', e);
    showToast('Delete failed: ' + (e.message || 'unknown error'), 'error');
  }
}

